import { Fragment } from 'react';
import type { ReactNode } from 'react';

/**
 * The small subset of markdown an arc body actually uses.
 *
 * Ported from `plan.html`'s `md()`, with one deliberate difference: that one
 * builds an HTML string and assigns innerHTML. Here we build React elements
 * instead. Not style — an innerHTML path turns any `<script>` that ever
 * reaches a vault file into script running on the origin that holds the
 * vault key, and this page's CSP is the last line of that defence rather
 * than the first.
 *
 * Arc prose leans on `[[Other arc]]` wiki links, which plan.html does not
 * render at all; they resolve here when the target exists.
 */

export interface MarkdownLinks {
  /** Wiki-link target → href, or null when no such arc exists. */
  resolveArc: (name: string) => string | null;
}

export function renderMarkdown(source: string, links: MarkdownLinks): ReactNode {
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let fence: string[] | null = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push(<p key={blocks.length}>{inline(paragraph.join(' '), links)}</p>);
    paragraph = [];
  };
  const flushList = () => {
    if (!list.length) return;
    blocks.push(
      <ul key={blocks.length}>
        {list.map((item, i) => (
          <li key={i}>{inline(item, links)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  for (const line of source.split('\n')) {
    if (line.startsWith('```')) {
      if (fence) {
        blocks.push(
          <pre key={blocks.length}>
            <code>{fence.join('\n')}</code>
          </pre>,
        );
        fence = null;
      } else {
        flushParagraph();
        flushList();
        fence = [];
      }
      continue;
    }
    if (fence) {
      fence.push(line);
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1]!.length;
      const Tag = `h${Math.min(level + 1, 6)}` as 'h2' | 'h3' | 'h4' | 'h5';
      blocks.push(<Tag key={blocks.length}>{inline(heading[2]!, links)}</Tag>);
      continue;
    }

    const item = line.match(/^\s*[-*]\s+(.*)$/);
    if (item) {
      flushParagraph();
      list.push(item[1]!);
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();
  if (fence) blocks.push(<pre key={blocks.length}><code>{fence.join('\n')}</code></pre>);

  return blocks;
}

/** Inline spans, matched in one pass so a link's text cannot be re-scanned
 *  and a code span's contents stay literal. */
function inline(text: string, links: MarkdownLinks): ReactNode {
  const pattern = /`([^`]+)`|\*\*([^*]+)\*\*|\[\[([^\]]+)\]\]|\[([^\]]+)\]\(([^)]+)\)|==([^=]+)==/g;
  const out: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const [, code, bold, wiki, linkText, href, mark] = match;

    if (code !== undefined) out.push(<code key={key++}>{code}</code>);
    else if (bold !== undefined) out.push(<strong key={key++}>{bold}</strong>);
    else if (mark !== undefined) out.push(<mark key={key++}>{mark}</mark>);
    else if (wiki !== undefined) {
      const target = links.resolveArc(wiki);
      out.push(
        target ? (
          <a key={key++} href={target}>
            {wiki}
          </a>
        ) : (
          <span key={key++} className="wiki-unresolved">
            {wiki}
          </span>
        ),
      );
    } else if (linkText !== undefined && href !== undefined) {
      // Only http(s) becomes a link. A `javascript:` or `data:` href in a
      // vault file must render as text, not as something clickable.
      out.push(
        /^https?:\/\//.test(href) ? (
          <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        ) : (
          <Fragment key={key++}>{linkText}</Fragment>
        ),
      );
    }
    last = pattern.lastIndex;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}
